"use client"

import { DeductionListJson } from "@/api/deduction.tsx"
import { Badge } from "@/components/ui/badge.tsx"

import { cellCurrencyFormatter } from "@/deduction/[id]/backup_columns.tsx"
import { handleViewS3Uri } from "@/deduction/[id]/UserFiles.tsx"
import { AccessorColumnDef, Column, DisplayColumnDef, GroupColumnDef, RowSelectionInstance } from "@tanstack/react-table"

import { CategoryFilterHeader } from "@/deduction_table/CategoryFilterHeader.tsx"
import { DeductionListNextActionCell } from "@/deduction_next_action/deduction_next_action_list.tsx"
import { TableState } from "@/deduction_table/table_state"
import { displayFormattedImportSource, exactCurrencyFormatter, get_display_name, isSuperCoffeeUser } from "@/utils"
import { display_row_amount } from "@/utils/transaction_amount"
import { format_custom_deduction_method_label } from "@/utils/format_custom_deduction_method"
import { CopyableCell } from "@/components/copyable_cell.tsx"
import { differenceInDays, formatDistanceToNow } from "date-fns"
import {
  Check,
  CircleCheckIcon,
  ClipboardList,
  Copy,
  Dot,
  Ellipsis,
  Eye,
  File,
  FileCheck,
  FileCog,
  LoaderCircle,
  Paperclip,
  Receipt,
  Search,
  ShieldQuestion,
  Trophy,
  Wand2,
} from "lucide-react"
import { CategoryCell } from "@/deduction_table/CategoryCell"
import { StatusSelect } from "./status_select.tsx"
import { ListActionButtons } from "./actions"
import { getAmazonPaymentRemittanceLink } from "./actions/amazon_utils"
import { ColumnHeader } from "./column_header.tsx"
import { createDateColumn } from "./date_column.tsx"
import { Checkbox } from "@/components/ui/checkbox.tsx"
import {
  StageMultiSelect,
  parse_status_column_filter,
  serialize_status_column_filter,
} from "./stage_multi_select.tsx"
import { current_days_old_basis, use_days_old_basis } from "@/deduction/aging/aging_state"
import {
  AgeBasis,
  age_bucket_filter,
  bucket_for_days,
  compute_age_days,
  days_old_column_title,
  format_days_old_badge,
  format_days_old_detail,
  is_age_bucket_filter_value,
} from "@/utils/deduction_age"
import { defaultFilter, split_filter_list_input } from "@/utils/filters.ts"
import { TriageInProgressState, BigWizInProgressState } from "@/deduction_table/triage_state"

export const STATUS_ICONS = {
  new: <Dot className="h-4 w-4 text-orange-700" />,
  review: <Eye className="h-4" />,
  task_assigned: <ClipboardList className="h-4 text-yellow-600" />,
  validated: <CircleCheckIcon fill="green" className="h-4 text-white" />,
  expensable: <Dot className="h-4 text-green-500" />,
  won: <Trophy className="h-4" />,
  pending: <Ellipsis className="h-4" />,
  disputed: <Ellipsis className="h-4" />,
  backup_requested: <FileCog className="h-4" />,
  backup_received: <FileCheck className="h-4" />,
  processing: <Receipt className="h-4" />,
  disputable: <ShieldQuestion className="h-4" />,
}

export const selectColumn: DeductionTableColumn = {
  id: "select",
  header: ({ table }: { table: RowSelectionInstance<DeductionListJson> }) => (
    <Checkbox
      checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && "indeterminate")}
      onCheckedChange={(value: any) => table.toggleAllPageRowsSelected(!!value)}
      aria-label="Select all"
      className="h-4 w-4"
    />
  ),
  cell: ({ row }) => (
    <Checkbox
      checked={row.getIsSelected()}
      onClick={(e: MouseEvent) => {
        e.preventDefault()
        e.stopImmediatePropagation()
        row.toggleSelected()
      }}
      aria-label="Select row"
      className="h-4 w-4"
    />
  ),
  enableSorting: false,
  enableHiding: false,
  enableGlobalFilter: false,
}
// AI notes on filtering:
// The key difference lies in how the accessorKey and id fields are used in combination with accessorFn:
// When a column has an accessorKey, it automatically creates a default filter function that works with that key's value, even if there's an accessorFn. The accessorFn in this case is only used for display/sorting purposes.
// When a column has an id instead of an accessorKey, it relies entirely on the accessorFn for both display and filtering, which is why it needs an explicit filterFn.

export const invoiceAmountColumn: DeductionTableColumn = {
  accessorKey: "deduction_amount",
  enableHiding: false,
  header: ({ column }) => <ColumnHeader className="justify-end" column={column} title="Amount" />,
  cell: ({ row }) => (
    <div class="text-right font-medium">
      {exactCurrencyFormatter(
        display_row_amount(row.original.kind, row.original.invoice_amount, row.original.deduction_amount ?? row.original.invoice_amount)
      )}
    </div>
  ),
  filterFn: (row, id, value) => {
    const rowValue = row.original.deduction_amount
    const rawTokens = split_filter_list_input(value)
    if (rawTokens.length === 0) return true
    const numericTokens = rawTokens
      .map(t => parseFloat(String(t).replace(/[$,]/g, "")))
      .filter(n => !Number.isNaN(n))
    if (numericTokens.length === 0) return false
    return numericTokens.some(filterValue => rowValue > filterValue * 0.9 && rowValue < filterValue * 1.1)
  },
  sortingFn: (rowA, rowB, columnId) => {
    let a = rowA.original.deduction_amount
    let b = rowB.original.deduction_amount
    return Math.abs(a) - Math.abs(b)
  },
}

export const discountAmountColumn: DeductionTableColumn = {
  accessorKey: "discount_amount",
  enableHiding: false,
  accessorFn: row => row.discount_amount || 0,
  header: ({ column }) => <ColumnHeader className="justify-end" column={column} title="Discount Amount" />,
  cell: cellCurrencyFormatter,
  filterFn: defaultFilter,
  sortingFn: (rowA, rowB, columnId) => {
    const a = parseFloat(rowA.getValue(columnId) as string) || 0
    const b = parseFloat(rowB.getValue(columnId) as string) || 0
    return a - b
  },
}

function paymentDaysBadgeVariant(earlyPayWindow: DeductionListJson["early_pay_window"]): "won" | "review" | "new" | "outline" {
  if (earlyPayWindow === "within") return "won"
  if (earlyPayWindow === "debatable") return "review"
  if (earlyPayWindow === "outside") return "new"
  return "outline"
}

export const paymentDaysColumn: DeductionTableColumn = {
  id: "payment_days",
  accessorFn: row => {
    if (row.early_pay_days != null && row.early_pay_days !== undefined) {
      return row.early_pay_days
    }
    if (!row.check_date) return null

    const invoiceDate = new Date(row.invoice_date)
    const checkDate = new Date(row.check_date)
    return differenceInDays(checkDate, invoiceDate)
  },
  header: ({ column }) => <ColumnHeader column={column} title="Payment Days" />,
  cell: ({ getValue, row }) => {
    const data = row.original as DeductionListJson
    // Hy-Vee counts terms from DC receipt; days from the invoice date would be the wrong number.
    if (data.early_pay_window === "needs_receipt_date") {
      return (
        <Badge variant="outline" title="Hy-Vee counts early-pay terms from DC receipt of the PO; this invoice has no receipt date.">
          No receipt date
        </Badge>
      )
    }
    const raw = getValue() as number | null
    const days = raw
    if (days == null || Number.isNaN(days)) return null

    const variant = paymentDaysBadgeVariant(data.early_pay_window)
    const title =
      data.early_pay_window != null
        ? "Colored by org integration early pay terms (Kehe/UNFI discount term)."
        : undefined

    // @ts-ignore
    return (
      <Badge variant={variant} title={title}>
        {days} days
      </Badge>
    )
  },
  filterFn: defaultFilter,
}

/** Red on the Days Old badge means "act now", not "this is old". */
const DAYS_OLD_RED_EXPIRY_THRESHOLD = 45

export function days_old_badge_variant(
  days: number,
  days_until_expiry: number | null,
  basis: AgeBasis = "invoice_date"
): "outline" | "warning" | "review" | "new" {
  // When the column is showing days-until-expiry, the value itself is the urgency signal.
  if (basis === "until_dispute_expiration") {
    return days <= DAYS_OLD_RED_EXPIRY_THRESHOLD ? "new" : "outline"
  }
  // Red is reserved for genuine urgency: the dispute window closes within 45 days (or
  // already has). Age alone never goes past amber — a big red pile just looks bad.
  if (days_until_expiry != null && days_until_expiry <= DAYS_OLD_RED_EXPIRY_THRESHOLD) {
    return "new"
  }
  const bucket = bucket_for_days(days, "invoice_date")
  switch (bucket?.key) {
    case "31_60":
      return "warning"
    case "61_90":
    case "90_plus":
      return "review"
    default:
      return "outline"
  }
}

/**
 * Filter for the days_old column: AgingPanel sets an {basis, min, max} object (bucket
 * click-to-filter, any age basis); anything else (e.g. text typed in the column filter or a
 * stale persisted value) falls back to plain text matching on the displayed day count.
 */
export function days_old_filter(row: any, id: string, value: unknown): boolean {
  if (is_age_bucket_filter_value(value)) {
    return age_bucket_filter(row.original, value)
  }
  return defaultFilter(row, id, value)
}

function DaysOldHeader({ column }: { column: Column<DeductionListJson, unknown> }) {
  const basis = use_days_old_basis()
  return <ColumnHeader column={column} title={days_old_column_title(basis)} />
}

function DaysOldCell({ row }: { row: DeductionListJson }) {
  const basis = use_days_old_basis()
  const days = compute_age_days(row, basis)
  if (days == null || Number.isNaN(days)) return null
  const days_until_expiry =
    basis === "until_dispute_expiration" ? days : compute_age_days(row, "until_dispute_expiration")
  const variant = days_old_badge_variant(days, days_until_expiry, basis)
  return (
    <Badge variant={variant} title={format_days_old_detail(days, basis)}>
      {format_days_old_badge(days, basis)}
    </Badge>
  )
}

export const daysOldColumn: DeductionTableColumn = {
  id: "days_old",
  // Always mirrors the Aging panel "Age by" basis (including days until dispute expires).
  accessorFn: row => compute_age_days(row, current_days_old_basis()) ?? undefined,
  header: ({ column }) => <DaysOldHeader column={column} />,
  enableGlobalFilter: false,
  sortUndefined: "last",
  cell: ({ row }) => <DaysOldCell row={row.original} />,
  filterFn: days_old_filter,
}

export const daysInStageColumn: DeductionTableColumn = {
  id: "days_in_stage",
  accessorFn: row => compute_age_days(row, "in_current_stage") ?? undefined,
  header: ({ column }) => <ColumnHeader column={column} title="Days in Stage" />,
  enableGlobalFilter: false,
  sortUndefined: "last",
  cell: ({ getValue }) => {
    const days = getValue() as number | undefined
    if (days == null || Number.isNaN(days)) return null
    return <Badge variant="outline">{days} days</Badge>
  },
  filterFn: defaultFilter,
}

/** Pure display logic for the dispute_expires cell (exported for tests). */
export function dispute_expiry_display(days: number | null | undefined): { text: string; urgent: boolean } | null {
  if (days == null || Number.isNaN(days)) return null
  if (days < 0) return { text: "Expired", urgent: true }
  if (days <= 30) return { text: `${days} days left`, urgent: true }
  return { text: `${days} days`, urgent: false }
}

export const disputeExpiresColumn: DeductionTableColumn = {
  id: "dispute_expires",
  accessorFn: row => compute_age_days(row, "until_dispute_expiration") ?? undefined,
  header: ({ column }) => <ColumnHeader column={column} title="Dispute Expires" />,
  enableGlobalFilter: false,
  sortUndefined: "last",
  cell: ({ getValue }) => {
    const display = dispute_expiry_display(getValue() as number | undefined)
    if (!display) return null
    if (display.urgent) {
      return <Badge variant="destructive">{display.text}</Badge>
    }
    return <span class="text-muted-foreground">{display.text}</span>
  },
  filterFn: defaultFilter,
}

export const invoiceNumberColumn: DeductionTableColumn = {
  accessorKey: "invoice_number",
  enableHiding: false,
  header: ({ column }) => <ColumnHeader column={column} title="Invoice Number" />,
  filterFn: defaultFilter,
  cell: ({ getValue }) => {
    const value = getValue() as string
    return <CopyableCell value={value} />
  },
}

export const importSourceColumn: DeductionTableColumn = {
  accessorKey: "import_source",
  header: ({ column }) => <ColumnHeader column={column} title="Import Source" />,
  accessorFn: row => displayFormattedImportSource(row.import_source),
  filterFn: defaultFilter,
}

export const customerColumn: DeductionTableColumn = {
  accessorKey: "customer_name",
  enableHiding: false,
  header: ({ column }) => <ColumnHeader column={column} title="Customer" />,
  filterFn: defaultFilter,
}

// The deduction `source` (e.g. "kehe", "unfi") — the same field the global
// distributor filter and the Deduction Trends chart group by. Hidden by default;
// exposed so a multi-customer filter (exact-match, OR across a token list) can be
// applied programmatically, e.g. from the trend chart's "View deductions" drill-down.
export const sourceColumn: DeductionTableColumn = {
  accessorKey: "source",
  enableHiding: true,
  header: ({ column }) => <ColumnHeader column={column} title="Customer (source)" />,
  cell: ({ getValue }) => <span>{get_display_name(String(getValue() ?? ""))}</span>,
  filterFn: (row, id, value) => {
    const tokens = split_filter_list_input(value)
    if (tokens.length === 0) return true
    const cell = String(row.getValue(id) ?? "").toLowerCase()
    return tokens.some(token => token.toLowerCase() === cell)
  },
}

export const sourceNumberColumn: DeductionTableColumn = {
  id: "source_number",
  accessorFn: row => {
    if (row.original_source) {
      return row.original_source.replace(/\D/g, "")
    }
    return ""
  },
  header: ({ column }) => <ColumnHeader column={column} title="Customer #" />,
  enableHiding: true,
  filterFn: defaultFilter,
}

export const dcColumn: DeductionTableColumn = {
  accessorKey: "dc",
  header: ({ column }) => <ColumnHeader column={column} title="DC" />,
  filterFn: defaultFilter,
}

export const dcNameColumn: DeductionTableColumn = {
  accessorKey: "dc_name",
  header: ({ column }) => <ColumnHeader column={column} title="DC Name" />,
  enableGlobalFilter: false,
  filterFn: defaultFilter,
}

function StageColumnFilter({ column }: { column: Column<DeductionListJson, unknown> }) {
  return (
    <ColumnHeader
      column={column}
      title="Stage"
      filterContent={
        <StageMultiSelect
          selected={parse_status_column_filter(column.getFilterValue())}
          onChange={stages => column.setFilterValue(serialize_status_column_filter(stages))}
        />
      }
    />
  )
}

/** Action header filters by stage (same multi-select as the Stage column / Aging panel). */
function ActionColumnStageFilter({ column }: { column: Column<DeductionListJson, unknown> }) {
  const tableState = TableState.use()
  const status_filter = tableState.columnFilters.find(f => f.id === "status_value")?.value
  return (
    <ColumnHeader
      column={column}
      title="Action"
      filterContent={
        <StageMultiSelect
          selected={parse_status_column_filter(status_filter)}
          onChange={stages => {
            const value = serialize_status_column_filter(stages)
            TableState.set(s => ({
              ...s,
              columnFilters: [
                ...s.columnFilters.filter(f => f.id !== "status_value"),
                ...(value != null ? [{ id: "status_value", value }] : []),
              ],
            }))
          }}
        />
      }
    />
  )
}

export const statusColumn: DeductionTableColumn = {
  accessorKey: "status_value",
  header: ({ column }) => <StageColumnFilter column={column} />,
  cell: cell => {
    if (cell.row.original.kind === "prepayment") {
      return null
    } else {
      return (
        <div class="flex justify-center">
          <StatusSelect deduction={cell.row.original} />
        </div>
      )
    }
  },
  filterFn: defaultFilter,
}

export const actionColumn: DeductionTableColumn = {
  accessorKey: "action",
  header: ({ column }) => <ActionColumnStageFilter column={column} />,
  cell: cell => {
    let deduction = cell.row.original
    return (
      <div class="flex justify-center">
        <DeductionListNextActionCell deduction={deduction} />
      </div>
    )
  },
}

function countFiles(ded: DeductionListJson): number {
  let count = 0
  if (ded.backup_s3_uri) count++
  if (ded.check_s3_uri) count++
  if (ded.user_files) count += ded.user_files.length
  return count
}

/** Row shape for backup parse column (deduction list + accounting transactions). */
export interface BackupParseRow {
  backup_parsed?: boolean
  backup_s3_uri: string | null
}

/** Sort key: higher = parsed; PDF without lines sorts above rows with no PDF. */
export function backup_parse_sort_rank(row: BackupParseRow): number {
  const parsed = row.backup_parsed === true
  const pdf = (row.backup_s3_uri ?? "").toLowerCase().endsWith(".pdf")
  if (parsed) return 1000
  if (pdf) return 1
  return 0
}

export function backup_parse_cell_label(row: BackupParseRow): { text: string; title: string } {
  const parsed = row.backup_parsed === true
  const pdf = (row.backup_s3_uri ?? "").toLowerCase().endsWith(".pdf")
  if (parsed) {
    return {
      text: "Parsed",
      title: "At least one backup line item exists from a successful parse.",
    }
  }
  if (pdf) {
    return {
      text: "Not parsed",
      title: "A PDF backup is set on the deduction but no backup line items exist yet. Use Parse backup on the detail page or bulk More → Parse backup.",
    }
  }
  return {
    text: "—",
    title: "No PDF backup on the deduction (canonical backup_s3_uri).",
  }
}

export const backupParseColumn: DeductionTableColumn = {
  id: "backup_parse",
  accessorFn: (row: DeductionListJson) => backup_parse_sort_rank(row),
  header: ({ column }) => <ColumnHeader column={column} title="Backup parsed" />,
  enableSorting: true,
  enableHiding: true,
  cell: ({ row }) => {
    const { text, title } = backup_parse_cell_label(row.original)
    const parsed = row.original.backup_parsed === true
    if (parsed) {
      return (
        <span class="inline-flex" title={title}>
          <Badge variant="outline" className="text-emerald-800 border-emerald-300 bg-emerald-50">
            {text}
          </Badge>
        </span>
      )
    }
    if ((row.original.backup_s3_uri ?? "").toLowerCase().endsWith(".pdf")) {
      return (
        <span class="inline-flex" title={title}>
          <Badge variant="outline" className="text-amber-900 border-amber-300 bg-amber-50">
            {text}
          </Badge>
        </span>
      )
    }
    return (
      <span class="text-muted-foreground tabular-nums" title={title}>
        {text}
      </span>
    )
  },
}

/** Row shape for the promotion match column (deduction list + accounting transactions). */
export interface PromotionMatchRow {
  /** Server flag: matched directly (deduction_promotion_match) or via a coded split's promotion. */
  promotion_matched?: boolean
}

export function is_promotion_matched(row: PromotionMatchRow): boolean {
  return row.promotion_matched === true
}

/** Sort key so matched rows group together. */
export function promotion_match_sort_rank(row: PromotionMatchRow): number {
  return is_promotion_matched(row) ? 1 : 0
}

/** Pure filter rule for the matched / unmatched header choices. */
export function matches_promotion_filter(row: PromotionMatchRow, value: unknown): boolean {
  if (value == null || value === "" || value === "all") return true
  if (value === "matched") return is_promotion_matched(row)
  if (value === "unmatched") return !is_promotion_matched(row)
  return true
}

export const PROMOTION_MATCH_FILTER_CHOICES = ["matched", "unmatched"]

export const promotionMatchColumn: DeductionTableColumn = {
  id: "promotion_match",
  meta: { columnPickerLabel: "Promo matched" },
  accessorFn: (row: DeductionListJson) => promotion_match_sort_rank(row),
  header: ({ column }) => (
    <ColumnHeader
      column={column}
      title="Promo"
      choices={PROMOTION_MATCH_FILTER_CHOICES}
      onSelectChoice={(choice: string) => column.setFilterValue(choice === "all" ? undefined : choice)}
    />
  ),
  enableSorting: true,
  enableHiding: true,
  enableGlobalFilter: false,
  filterFn: (row, _id, value) => matches_promotion_filter(row.original, value),
  cell: ({ row }) => {
    if (is_promotion_matched(row.original)) {
      return (
        <span class="inline-flex" title="Matched to a promotion (directly or through its account coding).">
          <Badge variant="outline" className="text-indigo-800 border-indigo-300 bg-indigo-50">
            Matched
          </Badge>
        </span>
      )
    }
    return (
      <span class="text-muted-foreground tabular-nums" title="No promotion is linked to this deduction yet.">
        —
      </span>
    )
  },
}

export const filesColumn: DeductionTableColumn = {
  id: "files", // named this so it's consistent in the column dropdown filter
  accessorFn: row => countFiles(row),
  header: ({ column }) => <ColumnHeader column={column} title="Files" />,
  enableGlobalFilter: false,
  enableSorting: true,
  filterFn: defaultFilter,
  sortingFn: (rowA, rowB) => {
    return countFiles(rowA.original) - countFiles(rowB.original)
  },
  cell: cell => {
    let ded = cell.row.original
    let buttons = []
    let backup_s3_uri = ded.backup_s3_uri
    if (backup_s3_uri) {
      buttons.push(
        <div onClick={handleViewS3Uri(backup_s3_uri)} className="cursor-pointer text-black underline pr-2">
          <Paperclip className="w-4 h-4" />
        </div>
      )
    } else if (ded.has_backup) {
      buttons.push(
        <div
          onClick={() => (window.location.href = `/deduction/${ded.id}`)}
          className="cursor-pointer text-black underline pr-2">
          <Paperclip className="w-4 h-4" />
        </div>
      )
    }
    let check_s3_uri = ded.check_s3_uri
    if (check_s3_uri) {
      buttons.push(
        <div onClick={handleViewS3Uri(check_s3_uri)} className="cursor-pointer text-black underline pr-2">
          <File className="w-4 h-4" />
        </div>
      )
    }

    if (ded.user_files) {
      for (const user_file of ded.user_files) {
        buttons.push(
          <div onClick={handleViewS3Uri(user_file.s3_uri)} className="cursor-pointer text-black underline pr-2">
            <FileCog className="w-4 h-4" />
          </div>
        )
      }
    }

    // if super & not kehe / unfi, link to google drive search with a magnifying glass icon
    if (isSuperCoffeeUser(ded.org_id) && !["kehe", "unfi"].includes(ded.source.toLowerCase())) {
      buttons.push(
        <a
          href={`https://drive.google.com/drive/search?q=${encodeURIComponent(ded.invoice_number)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="cursor-pointer text-primary underline pr-2">
          <Search className="w-4 h-4" />
        </a>
      )
    }
    if (!buttons.length) return ""
    return <div class="flex">{buttons}</div>
  },
}

/** Row shape for the backup column (deduction list + check detail transactions). */
export interface BackupPresenceRow {
  backup_s3_uri: string | null
  has_backup?: boolean
  backup_parsed?: boolean
  source?: string
}

export type BackupPresence = "file" | "lines" | "none"

/**
 * Whether a row has backup: a file we can open, only parsed/received line items, or nothing.
 * Mirrors the server's `has_actual_backup` — a UNFI row's `has_backup` flag does not count on
 * its own, because UNFI marks it from the remittance rather than from a document.
 */
export function backup_presence(row: BackupPresenceRow): BackupPresence {
  if (row.backup_s3_uri) return "file"
  if (row.backup_parsed) return "lines"
  if (row.has_backup && (row.source ?? "").toLowerCase() !== "unfi") return "lines"
  return "none"
}

export const BACKUP_FILTER_CHOICES = ["has backup", "no backup"]

export function matches_backup_filter(row: BackupPresenceRow, value: unknown): boolean {
  const has = backup_presence(row) !== "none"
  if (value === "has backup") return has
  if (value === "no backup") return !has
  return true
}

export const backupColumn: DeductionTableColumn = {
  id: "backup",
  accessorFn: (row: DeductionListJson) => (backup_presence(row) === "none" ? 0 : 1),
  header: ({ column }) => (
    <ColumnHeader
      column={column}
      title="Backup"
      choices={BACKUP_FILTER_CHOICES}
      onSelectChoice={choice => column.setFilterValue(choice === "all" ? undefined : choice)}
    />
  ),
  enableGlobalFilter: false,
  enableSorting: true,
  filterFn: (row, _columnId, value) => matches_backup_filter(row.original, value),
  sortingFn: (rowA, rowB) => backup_presence(rowA.original).localeCompare(backup_presence(rowB.original)),
  cell: ({ row }) => {
    const presence = backup_presence(row.original)
    if (presence === "file") {
      return (
        <div
          onClick={handleViewS3Uri(row.original.backup_s3_uri!)}
          className="inline-flex cursor-pointer text-black underline"
          title="Open the backup file">
          <Paperclip className="w-4 h-4" />
        </div>
      )
    }
    if (presence === "lines") {
      return (
        <span class="inline-flex text-emerald-700" title="Backup line items exist, but no file is attached">
          <FileCheck className="w-4 h-4" />
        </span>
      )
    }
    return <span class="text-gray-300">—</span>
  },
}

export const actionsColumn: DeductionTableColumn = {
  id: "actions",
  header: ({ column }) => <ColumnHeader column={column} title="Quick Links" />,
  enableGlobalFilter: false,
  cell: ({ row }) => {
    return <ListActionButtons deduction={row.original} />
  },
}

export const taskColumn: DeductionTableColumn = {
  id: "task",
  accessorFn: row => `${row.task?.status}: ${row.task?.user_display_name ?? get_display_name(row.task?.user_email)}`,
  header: ({ column }) => <ColumnHeader column={column} title="Task" />,
  enableSorting: true,
  cell: ({ row }) => {
    let task = row.original.task
    if (!task) return ""

    let task_assignee_name = task.user_display_name ?? get_display_name(task.user_email)
    // Surface how many automatic reminders the assignee has already received.
    let reminders = task.bump_count ? ` · ${task.bump_count} reminder${task.bump_count === 1 ? "" : "s"}` : ""
    return `${task.status}: ${task_assignee_name}${reminders}`
  },
  filterFn: defaultFilter,
}

export const taskAssignedAtColumn: DeductionTableColumn = {
  id: "task_assigned_at",
  accessorFn: row => row.task?.created_at,
  header: ({ column }) => <ColumnHeader column={column} title="Assigned" />,
  cell: ({ getValue }) => {
    const value = getValue() as string
    if (!value) return ""
    return formatDistanceToNow(new Date(value), { addSuffix: true })
  },
  // @ts-ignore
  filterFn: "dateFilter" as const,
}

export const checkAmountColumn: DeductionTableColumn = {
  accessorKey: "check_amount",
  header: ({ column }) => <ColumnHeader className="justify-end" column={column} title="Check Amount" />,
  cell: cellCurrencyFormatter,
  filterFn: defaultFilter,
}

export const categoryColumn: DeductionTableColumn = {
  accessorKey: "category",
  header: ({ column }) => (
    <CategoryFilterHeader
      column={column}
      onSelectChoice={(choice: string) => {
        // Use the column instance so the filter lands in whichever table is
        // rendering this column (deduction list, check detail, etc.)
        column.setFilterValue(choice === "all" ? undefined : choice)
      }}
    />
  ),
  cell: ({ row }) => <CategoryCell deduction={row.original} />,
  filterFn: defaultFilter,
}

export const categoryDescriptionColumn: DeductionTableColumn = {
  accessorKey: "category_description",
  header: ({ column }) => <ColumnHeader column={column} title="Description" />,
  enableGlobalFilter: true,
  filterFn: defaultFilter,
}

const remarksColumn: DeductionTableColumn = {
  accessorKey: "description",
  header: ({ column }) => <ColumnHeader column={column} title="Desc." />,
  filterFn: defaultFilter,
}

export const retailerColumn: DeductionTableColumn = {
  accessorKey: "retailer_name",
  header: ({ column }) => <ColumnHeader column={column} title="Retailer" />,
  filterFn: defaultFilter,
}

// export const retailerInvoiceNumberColumn: DeductionTableColumn = {
//   accessorKey: "retailer_invoice_number",
//   header: ({ column }) => <ColumnHeader column={column} title="Retailer Invoice Number" />,
// }

export const checkNumberColumn: DeductionTableColumn = {
  accessorKey: "check_number",
  header: ({ column }) => <ColumnHeader className="justify-end" column={column} title="Check Number" />,
  filterFn: defaultFilter,
  cell: ({ getValue, row }) => {
    const value = getValue() as string
    if (!value) return null

    // For Amazon deductions, make the check number clickable to payment details
    if (row.original.source.toLowerCase() === "amazon" && /^\d+$/.test(value)) {
      const paymentLink = getAmazonPaymentRemittanceLink(row.original)
      if (paymentLink) {
        return (
          <CopyableCell value={value}>
            <a
              href={paymentLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="text-primary hover:underline">
              {value}
            </a>
          </CopyableCell>
        )
      }
    }

    return <CopyableCell value={value} />
  },
}

export const customDeductionMethodColumn: DeductionTableColumn = {
  accessorKey: "custom_deduction_method",
  header: ({ column }) => <ColumnHeader column={column} title="Deduction Method" />,
  enableHiding: true,
  filterFn: defaultFilter,
  cell: ({ getValue }) => {
    const raw = getValue() as string | null | undefined
    const label = format_custom_deduction_method_label(raw)
    const rawStr = raw != null && raw.trim() !== "" ? raw.trim() : ""
    if (!rawStr) {
      return <span class="text-sm text-gray-500">{label}</span>
    }
    return (
      <CopyableCell value={rawStr}>
        <span class="text-sm">{label}</span>
      </CopyableCell>
    )
  },
}

export const accountingStatusColumn: DeductionTableColumn = {
  accessorKey: "accounting_status",
  header: ({ column }) => <ColumnHeader column={column} title="Accounting Status" />,
  filterFn: defaultFilter,
}

export const hasSplitsColumn: DeductionTableColumn = {
  accessorKey: "has_splits",
  header: ({ column }) => <ColumnHeader column={column} title="Split" />,
  accessorFn: row => (row.has_splits ? "Yes" : "No"),
  filterFn: defaultFilter,
}

export const triageStatusColumn: DeductionTableColumn = {
  id: "triage_status",
  meta: { columnPickerLabel: "Wiz" },
  header: ({ column }) => <ColumnHeader column={column} title="Wiz" />,
  enableGlobalFilter: false,
  enableSorting: false,
  cell: ({ row }) => {
    const triageInProgress = TriageInProgressState.use() ?? new Set<string>()
    const bigWizInProgress = BigWizInProgressState.use() ?? new Set<string>()
    const deductionId = row.original.id

    // BigWiz running: light-blue spinner (takes priority over a finished triage).
    if (bigWizInProgress.has(deductionId)) {
      return (
        <div class="flex justify-center">
          <LoaderCircle className="h-4 w-4 animate-spin text-sky-400" />
        </div>
      )
    }

    if (triageInProgress.has(deductionId)) {
      return (
        <div class="flex justify-center">
          <LoaderCircle className="h-4 w-4 animate-spin text-purple-600" />
        </div>
      )
    }

    function openDeduction(e: MouseEvent, query: string) {
      e.stopPropagation()
      if (e.metaKey) {
        window.open(`/deduction/${deductionId}${query}`, "_blank")
      } else {
        window.location.href = `/deduction/${deductionId}${query}`
      }
    }

    // BigWiz has run: light-blue wand, click to open the deduction it ran on.
    if (row.original.has_bigwiz_result) {
      return (
        <div class="flex justify-center">
          <Wand2
            className="h-4 w-4 text-sky-400 cursor-pointer hover:text-sky-600"
            onClick={(e: MouseEvent) => openDeduction(e, "")}
          />
        </div>
      )
    }

    if (row.original.has_triage_result) {
      return (
        <div class="flex justify-center">
          <Wand2
            className="h-4 w-4 text-purple-600 cursor-pointer hover:text-purple-800"
            onClick={(e: MouseEvent) => openDeduction(e, "?wizard=true")}
          />
        </div>
      )
    }

    return null
  },
}

export const isParentColumn: DeductionTableColumn = {
  accessorKey: "is_parent",
  header: ({ column }) => <ColumnHeader column={column} title="Parent" />,
  accessorFn: row => (row.is_parent ? "Yes" : "No"),
}

export const lastTouchedAtColumn: DeductionTableColumn = createDateColumn("last_activity_at", "Last Activity", {
  enableHiding: true,
})

const poNumberColumn: DeductionTableColumn = {
  accessorKey: "po_number",
  header: ({ column }) => <ColumnHeader column={column} title="PO " />,
  enableSorting: true,
  filterFn: defaultFilter,
  cell: ({ getValue }) => {
    const value = getValue() as string
    return <CopyableCell value={value} />
  },
}

export type TableColumn<T> =
  | DisplayColumnDef<T, unknown>
  | GroupColumnDef<T, unknown>
  | (AccessorColumnDef<T, unknown> & {
      accessorKey: keyof T
    })

export type DeductionTableColumn = TableColumn<DeductionListJson>

/** Deduction list columns except backup parse (append after org columns so it stays rightmost). */
export const BASE_COLUMNS_WITHOUT_BACKUP_PARSE: DeductionTableColumn[] = [
  selectColumn,
  createDateColumn("invoice_date", "Invoice Date", {
    enableGlobalFilter: true,
    enableHiding: false,
  }),
  customerColumn,
  sourceColumn,
  invoiceNumberColumn,
  categoryColumn,
  categoryDescriptionColumn,
  retailerColumn,
  invoiceAmountColumn,
  statusColumn,
  actionColumn,
  filesColumn,
  actionsColumn,
  triageStatusColumn,
  promotionMatchColumn,
  taskColumn,
  taskAssignedAtColumn,
  lastTouchedAtColumn,
  daysOldColumn,
  daysInStageColumn,
  disputeExpiresColumn,
  checkAmountColumn,
  checkNumberColumn,
  createDateColumn("execution_date", "Execution Date", {
    enableGlobalFilter: true,
    enableHiding: true,
  }),
  dcColumn,
  dcNameColumn,
  remarksColumn,
  createDateColumn("check_date", "Check Date", { enableGlobalFilter: true, enableHiding: true }),
  poNumberColumn,
  // pendingColumn,
  // SUPER COFFEE columns:
  // retailerInvoiceNumberColumn,
  // flagColumn,
  accountingStatusColumn,
  customDeductionMethodColumn,
  // customCategoryColumn,
  importSourceColumn,
  hasSplitsColumn,
  // isParentColumn,
  {
    accessorKey: "validated",
    enableHiding: true,
  },
]

export const BASE_COLUMNS: DeductionTableColumn[] = [...BASE_COLUMNS_WITHOUT_BACKUP_PARSE, backupParseColumn]

if (import.meta.vitest) {
  describe("deduction table description column", () => {
    it("uses the shared text filter for column-level search", () => {
      const column = BASE_COLUMNS.find((c: any) => c.accessorKey === "description") as any

      expect(column.filterFn).toBe(defaultFilter)
      expect(column.filterFn({ getValue: () => "Customer shortage allowance" }, "description", "shortage")).toBe(true)
      expect(column.filterFn({ getValue: () => null }, "description", "shortage")).toBe(false)
    })
  })

  describe("deduction table columns use defaultFilter where header search applies", () => {
    it("invoice_number uses defaultFilter", () => {
      const column = BASE_COLUMNS.find((c: any) => c.accessorKey === "invoice_number") as any
      expect(column.filterFn).toBe(defaultFilter)
    })

    it("category_description uses defaultFilter", () => {
      const column = BASE_COLUMNS.find((c: any) => c.accessorKey === "category_description") as any
      expect(column.filterFn).toBe(defaultFilter)
    })

    it("check_number uses defaultFilter", () => {
      const column = BASE_COLUMNS.find((c: any) => c.accessorKey === "check_number") as any
      expect(column.filterFn).toBe(defaultFilter)
    })
  })

  describe("backup_presence", () => {
    it("prefers the file, then line items, then nothing", () => {
      expect(backup_presence({ backup_s3_uri: "s3://x/a.pdf", backup_parsed: false })).toBe("file")
      expect(backup_presence({ backup_s3_uri: null, backup_parsed: true })).toBe("lines")
      expect(backup_presence({ backup_s3_uri: null, has_backup: true, source: "kehe" })).toBe("lines")
      expect(backup_presence({ backup_s3_uri: null })).toBe("none")
    })

    it("ignores UNFI's has_backup flag without a file or parsed lines", () => {
      expect(backup_presence({ backup_s3_uri: null, has_backup: true, source: "unfi" })).toBe("none")
      expect(backup_presence({ backup_s3_uri: null, has_backup: true, source: "UNFI", backup_parsed: true })).toBe("lines")
    })

    it("filters by presence and passes everything through for other values", () => {
      const with_file = { backup_s3_uri: "s3://x/a.pdf" }
      const without = { backup_s3_uri: null }
      expect(matches_backup_filter(with_file, "has backup")).toBe(true)
      expect(matches_backup_filter(without, "has backup")).toBe(false)
      expect(matches_backup_filter(without, "no backup")).toBe(true)
      expect(matches_backup_filter(with_file, undefined)).toBe(true)
    })
  })

  describe("backup_parse_sort_rank", () => {
    it("ranks parsed above pdf-only above none", () => {
      expect(backup_parse_sort_rank({ backup_parsed: true, backup_s3_uri: "s3://x/a.pdf" })).toBe(1000)
      expect(backup_parse_sort_rank({ backup_parsed: true, backup_s3_uri: null })).toBe(1000)
      expect(backup_parse_sort_rank({ backup_parsed: false, backup_s3_uri: "s3://x/y.PDF" })).toBe(1)
      expect(backup_parse_sort_rank({ backup_parsed: false, backup_s3_uri: null })).toBe(0)
    })
  })

  describe("promotion match column", () => {
    it("treats only an explicit true as matched", () => {
      expect(is_promotion_matched({ promotion_matched: true })).toBe(true)
      expect(is_promotion_matched({ promotion_matched: false })).toBe(false)
      expect(is_promotion_matched({})).toBe(false)
    })

    it("sorts matched rows above unmatched", () => {
      expect(promotion_match_sort_rank({ promotion_matched: true })).toBe(1)
      expect(promotion_match_sort_rank({ promotion_matched: false })).toBe(0)
      expect(promotion_match_sort_rank({})).toBe(0)
    })

    it("filters by matched / unmatched and passes everything otherwise", () => {
      const matched = { promotion_matched: true }
      const unmatched = { promotion_matched: false }
      expect(matches_promotion_filter(matched, "matched")).toBe(true)
      expect(matches_promotion_filter(unmatched, "matched")).toBe(false)
      expect(matches_promotion_filter(matched, "unmatched")).toBe(false)
      expect(matches_promotion_filter(unmatched, "unmatched")).toBe(true)
      expect(matches_promotion_filter(unmatched, "all")).toBe(true)
      expect(matches_promotion_filter(unmatched, undefined)).toBe(true)
      expect(matches_promotion_filter(unmatched, "")).toBe(true)
    })

    it("is wired into the deduction list columns", () => {
      expect(BASE_COLUMNS.some((c: any) => c.id === "promotion_match")).toBe(true)
    })
  })

  describe("days_old_badge_variant", () => {
    it("colors by aging bucket, capping at amber — age alone never goes red", () => {
      expect(days_old_badge_variant(0, null)).toBe("outline")
      expect(days_old_badge_variant(30, null)).toBe("outline")
      expect(days_old_badge_variant(31, null)).toBe("warning")
      expect(days_old_badge_variant(61, null)).toBe("review")
      expect(days_old_badge_variant(91, null)).toBe("review")
      expect(days_old_badge_variant(365, null)).toBe("review")
    })

    it("goes red only when the dispute window closes within 45 days (or is expired)", () => {
      expect(days_old_badge_variant(10, 45)).toBe("new")
      expect(days_old_badge_variant(10, 0)).toBe("new")
      expect(days_old_badge_variant(10, -5)).toBe("new")
      expect(days_old_badge_variant(365, 46)).toBe("review")
      expect(days_old_badge_variant(365, 200)).toBe("review")
    })

    it("on expiry basis, colors from days remaining directly", () => {
      expect(days_old_badge_variant(53, null, "until_dispute_expiration")).toBe("outline")
      expect(days_old_badge_variant(45, null, "until_dispute_expiration")).toBe("new")
      expect(days_old_badge_variant(-1, null, "until_dispute_expiration")).toBe("new")
    })
  })

  describe("days_old_filter", () => {
    it("applies age bucket filter for object values", () => {
      const today = new Date().toISOString().slice(0, 10)
      const row = { original: { invoice_date: today }, getValue: () => 0 }
      expect(days_old_filter(row, "days_old", { basis: "invoice_date", min: 0, max: 30 })).toBe(true)
      expect(days_old_filter(row, "days_old", { basis: "invoice_date", min: 31, max: 60 })).toBe(false)
    })

    it("falls back to text matching for plain values", () => {
      const row = { original: { invoice_date: "2024-01-01" }, getValue: () => 45 }
      expect(days_old_filter(row, "days_old", "45")).toBe(true)
      expect(days_old_filter(row, "days_old", "99")).toBe(false)
      expect(days_old_filter(row, "days_old", null)).toBe(true)
    })
  })

  describe("dispute_expiry_display", () => {
    it("covers expired, urgent, and normal states", () => {
      expect(dispute_expiry_display(null)).toBeNull()
      expect(dispute_expiry_display(undefined)).toBeNull()
      expect(dispute_expiry_display(-1)).toEqual({ text: "Expired", urgent: true })
      expect(dispute_expiry_display(0)).toEqual({ text: "0 days left", urgent: true })
      expect(dispute_expiry_display(30)).toEqual({ text: "30 days left", urgent: true })
      expect(dispute_expiry_display(31)).toEqual({ text: "31 days", urgent: false })
    })
  })
}
